import { HTTP_API_V2 } from '../../http-common'

// Formats a Date as `yyyy-MM-dd HH:mm:ss` (the format expected by the
// `alarms` endpoint, matching the Inobram App's NotificationRepository).
function formatApiDate (date) {
  const pad = value => String(value).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ` +
    `${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`
}

// Converts the app locale (e.g. `pt_BR`) to the locale format expected by the
// `translators/alarms` endpoint (e.g. `pt-BR`).
function toApiLanguage (locale) {
  return (locale || 'pt_BR').replace('_', '-')
}

export default {
  state: {
    alarmHistoryById: {},
    alarmHistoryCursor: null,
    alarmHistoryUpdatedAt: null,
    alarmTranslations: [],
    alarmHistoryLoading: false,
    alarmAnalysis: [],
    alarmAnalysisLoading: false
  },
  getters: {
    loadedAlarmHistory (state) {
      return Object.keys(state.alarmHistoryById)
        .map(key => state.alarmHistoryById[key])
        .sort((a, b) => {
          const aDate = new Date(a.rec_at || a.timestamp || 0).getTime()
          const bDate = new Date(b.rec_at || b.timestamp || 0).getTime()
          return bDate - aDate
        })
    },
    loadedAlarmTranslations (state) {
      return state.alarmTranslations
    },
    alarmHistoryLoading (state) {
      return state.alarmHistoryLoading
    },
    alarmHistoryUpdatedAt (state) {
      return state.alarmHistoryUpdatedAt
    },
    loadedAlarmAnalysis (state) {
      return state.alarmAnalysis
    },
    alarmAnalysisLoading (state) {
      return state.alarmAnalysisLoading
    }
  },
  mutations: {
    // Merges a batch of alarm records into the keyed history map. Records are
    // keyed by their unique `id` (falling back to a composite key) so repeated
    // or overlapping fetches never produce duplicates. The cursor advances to
    // the newest `rec_at` seen, which the next incremental fetch uses as its
    // lower bound.
    mergeAlarmHistory (state, records) {
      const map = Object.assign({}, state.alarmHistoryById)
      let cursor = state.alarmHistoryCursor
      let cursorMs = cursor ? new Date(cursor).getTime() : -Infinity
      records.forEach(record => {
        const key = record.id != null
          ? record.id
          : `${record.mac}|${record.rec_at}|${record.alarm != null ? record.alarm : record.code}`
        map[key] = record
        const recAt = record.rec_at || record.timestamp
        const recMs = new Date(recAt || 0).getTime()
        if (recAt && recMs > cursorMs) {
          cursor = recAt
          cursorMs = recMs
        }
      })
      state.alarmHistoryById = map
      state.alarmHistoryCursor = cursor
      state.alarmHistoryUpdatedAt = new Date().toISOString()
    },
    setAlarmTranslations (state, payload) {
      state.alarmTranslations = payload
    },
    setAlarmHistoryLoading (state, payload) {
      state.alarmHistoryLoading = payload
    },
    clearAlarmHistory (state) {
      state.alarmHistoryById = {}
      state.alarmHistoryCursor = null
      state.alarmHistoryUpdatedAt = null
    },
    setAlarmAnalysis (state, payload) {
      state.alarmAnalysis = payload
    },
    setAlarmAnalysisLoading (state, payload) {
      state.alarmAnalysisLoading = payload
    }
  },
  actions: {
    // Fetches the alarm history from the v2 API. Mirrors the App's
    // `GET alarms?date=...&barnId=...`. When `barnId` is omitted the backend
    // returns every alarm the user has access to (aggregated dashboard).
    //
    // When `payload.incremental` is true the request starts from the stored
    // cursor (newest `rec_at` already loaded) instead of clearing the list,
    // and the results are merged in. This lets a control-room panel keep the
    // History tab live without re-fetching the whole flock window or showing
    // duplicates: the keyed merge de-dups the boundary record(s) that the
    // inclusive `rec_at >=` filter returns again.
    fetchAlarmHistory ({ commit, state }, payload = {}) {
      const incremental = payload.incremental === true
      commit('clearError')
      if (!incremental) {
        commit('setAlarmHistoryLoading', true)
        commit('clearAlarmHistory')
      }

      let initialDate
      if (incremental && state.alarmHistoryCursor) {
        initialDate = new Date(state.alarmHistoryCursor)
      } else if (payload.date instanceof Date) {
        initialDate = payload.date
      } else {
        initialDate = new Date(Date.now() - 24 * 60 * 60 * 1000)
      }

      const params = { date: formatApiDate(initialDate) }
      if (payload.barnId) {
        params.barnId = payload.barnId
      }
      if (Array.isArray(payload.deviceIds) && payload.deviceIds.length > 0) {
        params.deviceIds = payload.deviceIds.join(',')
      }

      return new Promise((resolve, reject) => {
        HTTP_API_V2.get('alarms', { params })
          .then(result => {
            const records = Array.isArray(result.data) ? result.data : []
            commit('mergeAlarmHistory', records)
            resolve(records)
          })
          .catch(err => {
            commit('setError', err)
            reject(err)
          })
          .finally(() => {
            if (!incremental) {
              commit('setAlarmHistoryLoading', false)
            }
          })
      })
    },
    // Loads the alarm code -> description translations for the given locale.
    // Mirrors the App's `GET translators/alarms?language=...`.
    fetchAlarmTranslations ({ commit }, locale) {
      commit('clearError')
      return new Promise((resolve, reject) => {
        HTTP_API_V2.get('translators/alarms', {
          params: { language: toApiLanguage(locale) }
        })
          .then(result => {
            const translations = Array.isArray(result.data) ? result.data : []
            commit('setAlarmTranslations', translations)
            resolve(translations)
          })
          .catch(err => {
            commit('setAlarmTranslations', [])
            commit('setError', err)
            reject(err)
          })
      })
    },
    clearAlarmHistory ({ commit }) {
      commit('clearAlarmHistory')
    },
    // Fetches a dataset for the Alarm Analysis tab. Independent from
    // `fetchAlarmHistory` so the History and Analysis tabs never overwrite
    // each other's data. When `barnId` is omitted every accessible alarm is
    // returned (aggregated analysis across all barns).
    fetchAlarmAnalysis ({ commit }, payload = {}) {
      commit('clearError')
      commit('setAlarmAnalysisLoading', true)
      commit('setAlarmAnalysis', [])

      const initialDate = payload.date instanceof Date
        ? payload.date
        : new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)

      const params = { date: formatApiDate(initialDate) }
      if (payload.barnId) {
        params.barnId = payload.barnId
      }
      if (Array.isArray(payload.deviceIds) && payload.deviceIds.length > 0) {
        params.deviceIds = payload.deviceIds.join(',')
      }

      return new Promise((resolve, reject) => {
        HTTP_API_V2.get('alarms', { params })
          .then(result => {
            const analysis = (Array.isArray(result.data) ? result.data : [])
              .slice()
              .sort((a, b) => {
                const aDate = new Date(a.rec_at || a.timestamp || 0).getTime()
                const bDate = new Date(b.rec_at || b.timestamp || 0).getTime()
                return bDate - aDate
              })
            commit('setAlarmAnalysis', analysis)
            resolve(analysis)
          })
          .catch(err => {
            commit('setAlarmAnalysis', [])
            commit('setError', err)
            reject(err)
          })
          .finally(() => {
            commit('setAlarmAnalysisLoading', false)
          })
      })
    }
  }
}



// WEBPACK FOOTER //
// ./src/store/alarm_dashboard/index.js